import { Button } from '@ads/components-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/atoms';

import { USAGE_MAX_RANGE_DAYS } from '../../../utils/tutorAiLimits';

export interface UsagePanelStatusProps {
  /** Se o período do filtro pode ser consultado. Falso trava a consulta antes dela sair. */
  readonly isPeriodValid: boolean;
  /** Se a consulta do painel falhou. */
  readonly isError: boolean;
  /** Mensagem de falha do painel — cada aba descreve o que não carregou. */
  readonly errorMessage: string;
  /** Nova tentativa da consulta do painel. */
  readonly onRetry: () => void;
  /** Conteúdo exibido quando o período é válido e a consulta não falhou. */
  readonly children: ReactNode;
}

/**
 * Estado de carregamento compartilhado pelos painéis da aba de uso (`UsageOverviewPanel` e
 * `TokenUsagePanel`).
 *
 * Cada painel é dono da própria consulta, mas os dois navegam pelos mesmos três estados na
 * mesma ordem de precedência: período inválido vence erro, que vence o conteúdo. A ordem
 * importa porque com período inválido a query nem chega a disparar — mostrar o alerta de
 * falha nesse caso seria mentira, já que não houve tentativa de carregar nada. Centralizar
 * essa precedência aqui evita que os dois painéis divirjam silenciosamente (ex.: um deles
 * checando erro antes do período) numa mudança futura em só um dos dois.
 *
 * A mensagem de erro é a única parte que cada painel personaliza — ela descreve o que
 * especificamente não carregou (indicadores do período vs. consumo de tokens). O aviso de
 * período inválido e o rótulo do botão de nova tentativa são únicos e pertencem a este
 * componente, não a cada painel.
 */
export function UsagePanelStatus({
  isPeriodValid,
  isError,
  errorMessage,
  onRetry,
  children,
}: UsagePanelStatusProps) {
  const { t } = useTranslation('tutorAI');

  if (!isPeriodValid) {
    return (
      <Alert
        type="warning"
        description={t('usage.invalidPeriod', { maxDays: USAGE_MAX_RANGE_DAYS })}
      />
    );
  }

  if (isError) {
    return (
      <Alert type="danger">
        <div className="flex w-full flex-wrap items-center justify-between gap-mini">
          <span>{errorMessage}</span>
          <Button hierarchy="secondary" size="sm" onClick={onRetry}>
            {t('usage.retry')}
          </Button>
        </div>
      </Alert>
    );
  }

  return <>{children}</>;
}
